import {
  isSafeWebUrl,
  isWebFeedSourceCard,
  type SolWebFeedSourceCard,
  type SolWebPageSourceCard,
  type SolWebSourceCard,
} from '../../data/sol/sol-contract';

/**
 * A cited PUBLIC WEB source. M11 Checkpoint B / I-078, extended 2026-08-25.
 *
 * **THERE ARE TWO KINDS, AND THEY MUST NOT LOOK ALIKE.**
 *
 * A **page** is something Brandon can open, and it says so. A **live data
 * feed** — the weather, sport and market feeds the provider answers from — has
 * no page behind it, so it is shown, named and explicitly **not** presented as
 * something to tap. **No link, no invented URL, and no disabled button
 * pretending there is somewhere to go.**
 *
 * That distinction is enforced by the contract rather than by care: the feed
 * card type has no `url` field at all, so this component could not render a
 * link for one even if somebody later wanted it to.
 */
export interface SolWebSourceCardLinkProps {
  readonly card: SolWebSourceCard;
}

export function SolWebSourceCardLink({ card }: SolWebSourceCardLinkProps) {
  return isWebFeedSourceCard(card) ? <FeedCard card={card} /> : <PageCard card={card} />;
}

/**
 * A live data feed: named, visible, and honestly not openable.
 *
 * **IT IS NOT A LINK AND NOT A BUTTON.** `role="group"` with a label naming it
 * a live data source is what a screen reader announces, so the difference from
 * a page — which announces as a link — is audible as well as visible.
 */
function FeedCard({ card }: { readonly card: SolWebFeedSourceCard }) {
  return (
    <div
      className="sol-source sol-source--web sol-source--feed"
      role="group"
      aria-label={`Live data source ${card.name}. This source does not provide a public webpage.`}
    >
      <span className="sol-source-head">
        <span className="sol-source-title">{card.name}</span>
        <span className="sol-source-status">Live data</span>
      </span>
      <span className="sol-source-meta">This source does not provide a public webpage.</span>
    </div>
  );
}

/**
 * A public page.
 *
 * **THE URL IS CHECKED AGAIN HERE.** The server already refused anything that
 * is not `http(s)`, and this is the last boundary before it becomes something a
 * person can tap. A URL that does not pass is rendered as **plain text with no
 * link at all** rather than being silently dropped: he still sees what Sol
 * read, and there is still nothing to tap.
 *
 * **NOTHING HERE RENDERS MARKUP.** Title, host and excerpt are React text
 * children, so a page called `<img onerror=…>` is displayed as those
 * characters — the same discipline every other card follows.
 *
 * `target="_blank"` with `rel="noopener noreferrer"`: the opened page gets no
 * `window.opener` handle back into Crewlink and no referrer. On Android the
 * Capacitor WebView hands an external `http(s)` link to the system browser,
 * which is the behaviour we want — a public page is never rendered inside the
 * app's own origin.
 */
function PageCard({ card }: { readonly card: SolWebPageSourceCard }) {
  const safe = isSafeWebUrl(card.url);
  const meta = card.publishedAt === null ? card.host : `${card.host} · ${card.publishedAt}`;

  const body = (
    <>
      <span className="sol-source-head">
        <span className="sol-source-title">{card.title}</span>
        <span className="sol-source-status">Web</span>
      </span>
      <span className="sol-source-meta">{meta}</span>
      {card.excerpt ? <span className="sol-source-caption">{card.excerpt}</span> : null}
    </>
  );

  if (!safe) {
    // **REFUSED, AND SAID SO.** A link Crewlink will not open is not quietly
    // hidden — he is told the source could not be opened, which is honest and
    // is also the only way a bad URL is ever noticed.
    return (
      <div className="sol-source sol-source--web sol-source--unopenable">
        {body}
        <span className="sol-source-open">This source could not be opened safely.</span>
      </div>
    );
  }

  return (
    <a
      className="sol-source sol-source--web"
      href={card.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open web source ${card.title} on ${card.host}`}
    >
      {body}
      <span className="sol-source-open" aria-hidden="true">
        Open page →
      </span>
    </a>
  );
}
